import { InitOptions, InitOptionsJSON } from '@acsbe/widget-settings';
import {
  CustomInstallationConfig,
  customInstallationScript,
} from '../../../validations';
import { DEFAULT_WIDGET_CONFIG } from './hooks';

const iconMappingToWidget: Record<string, string> = {
  Blind: 'blind',
  Dog: 'dog',
  Help: 'help2',
  MagicWand: 'magic',
  Settings1: 'config',
  Settings2: 'config2',
  Toggles: 'toggles',
  Tools: 'tools',
  A: 'typeface',
  Wheelchair: 'wheels3',
  LegacyDisplay: 'display',
  LegacyDisplay2: 'display2',
  LegacyDisplay3: 'display3',
  LegacyHelp: 'help',
  LegacyPeople: 'people',
  LegacyPeople2: 'people2',
  LegacySettings: 'settings',
  LegacySettings2: 'settings2',
  LegacyWheels: 'wheels',
  LegacyWheels2: 'wheels2',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatWidgetConfig = (response?: any) => {
  if (!response) {
    return DEFAULT_WIDGET_CONFIG;
  }

  const formattedResponse = {
    leadColor: response.leadColor || DEFAULT_WIDGET_CONFIG.leadColor,
    language: response.language || DEFAULT_WIDGET_CONFIG.language,
    position: response.position || DEFAULT_WIDGET_CONFIG.position,
    triggerColor: response.triggerColor || DEFAULT_WIDGET_CONFIG.triggerColor,
    triggerPositionX:
      response.triggerPositionX || DEFAULT_WIDGET_CONFIG.triggerPositionX,
    triggerPositionY:
      response.triggerPositionY || DEFAULT_WIDGET_CONFIG.triggerPositionY,
    triggerSize: response.triggerSize || DEFAULT_WIDGET_CONFIG.triggerSize,
    triggerRadius:
      response.triggerRadius || DEFAULT_WIDGET_CONFIG.triggerRadius,
    hideTrigger:
      response.hideTrigger?.toString() ?? DEFAULT_WIDGET_CONFIG.hideTrigger,
    triggerOffsetX:
      response.triggerOffsetX?.toString() ||
      DEFAULT_WIDGET_CONFIG.triggerOffsetX,
    triggerOffsetY:
      response.triggerOffsetY?.toString() ||
      DEFAULT_WIDGET_CONFIG.triggerOffsetY,
    triggerIcon:
      Object.keys(iconMappingToWidget).find(
        (key) => iconMappingToWidget[key] === response.triggerIcon,
      ) || DEFAULT_WIDGET_CONFIG.triggerIcon,
    hideMobile:
      response.hideMobile?.toString() ?? DEFAULT_WIDGET_CONFIG.hideMobile,
    mobileTriggerPositionX:
      response.mobile?.triggerPositionX ||
      DEFAULT_WIDGET_CONFIG.mobileTriggerPositionX,
    mobileTriggerPositionY:
      response.mobile?.triggerPositionY ||
      DEFAULT_WIDGET_CONFIG.mobileTriggerPositionY,
    mobileTriggerSize:
      response.mobile?.triggerSize || DEFAULT_WIDGET_CONFIG.mobileTriggerSize,
    mobileTriggerRadius:
      response.mobile?.triggerRadius ||
      DEFAULT_WIDGET_CONFIG.mobileTriggerRadius,
    mobileTriggerOffsetX:
      response.mobile?.triggerOffsetX?.toString() ||
      DEFAULT_WIDGET_CONFIG.mobileTriggerOffsetX,
    mobileTriggerOffsetY:
      response.mobile?.triggerOffsetY?.toString() ||
      DEFAULT_WIDGET_CONFIG.mobileTriggerOffsetY,
    statementLink:
      response.statementLink || DEFAULT_WIDGET_CONFIG.statementLink,
    footerHtml: response.footerHtml || DEFAULT_WIDGET_CONFIG.footerHtml,
    consistentHelpData:
      response.consistentHelpData?.length > 0
        ? response.consistentHelpData
        : DEFAULT_WIDGET_CONFIG.consistentHelpData,
  };

  const result = customInstallationScript.safeParse(formattedResponse);
  if (result.success) {
    return formattedResponse;
  }

  console.warn('Validation errors:', result.error.errors);

  const validatedConfig: Record<string, unknown> = {};

  Object.keys(formattedResponse).forEach((key) => {
    const typedKey = key as keyof typeof formattedResponse;
    const value = (formattedResponse as Partial<typeof formattedResponse>)[
      typedKey
    ];

    try {
      const fieldSchema = customInstallationScript.shape[typedKey];
      validatedConfig[key] =
        fieldSchema?.parse(value) ?? DEFAULT_WIDGET_CONFIG[typedKey];
    } catch {
      console.log(
        `Invalid value for setting ${key}. got value: ${value}. assigning default value ${DEFAULT_WIDGET_CONFIG[typedKey]}`,
      );

      validatedConfig[key] = DEFAULT_WIDGET_CONFIG[typedKey];
    }
  });

  return validatedConfig as CustomInstallationConfig;
};

export const reverseFormatWidgetConfig = (
  config: CustomInstallationConfig,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  // import { InitOptionsJSON } from "@acsbe/widget-settings";
  const triggerIcon =
    iconMappingToWidget[config.triggerIcon] ??
    config.triggerIcon ??
    iconMappingToWidget[DEFAULT_WIDGET_CONFIG.triggerIcon];
  const mobileConfig = {
    triggerPositionX: config.mobileTriggerPositionX,
    triggerPositionY: config.mobileTriggerPositionY,
    triggerSize: config.mobileTriggerSize,
    triggerRadius: config.mobileTriggerRadius,
    triggerOffsetX: parseInt(config.mobileTriggerOffsetX),
    triggerOffsetY: parseInt(config.mobileTriggerOffsetY),
  };

  const hasMobileConfig = Object.values(mobileConfig).some(
    (value) => value !== undefined,
  );

  config.consistentHelpData = Array.isArray(config.consistentHelpData)
    ? config.consistentHelpData.filter(
        (item) => item.title?.trim() !== '' && item.description?.trim() !== '',
      )
    : [];

  return {
    leadColor: config.leadColor,
    language: config.language,
    position: config.position,
    triggerColor: config.triggerColor,
    triggerPositionX: config.triggerPositionX,
    triggerPositionY: config.triggerPositionY,
    triggerSize: config.triggerSize,
    triggerRadius: config.triggerRadius,
    hideTrigger: config.hideTrigger === 'true',
    triggerOffsetX: parseInt(config.triggerOffsetX),
    triggerOffsetY: parseInt(config.triggerOffsetY),
    triggerIcon,
    hideMobile: config.hideMobile === 'true',
    mobile: hasMobileConfig ? mobileConfig : undefined,
    statementLink: config.statementLink,
    footerHtml: config.footerHtml,
    consistentHelpData: config.consistentHelpData,
  };
};

export const validate = (initOptions: Partial<InitOptionsJSON>) => {
  const invalidKeys: string[] = [];

  function validateRecursive(
    options: Partial<InitOptionsJSON>,
    path: string[] = [],
  ) {
    if (typeof options !== 'object' || options == null) {
      return;
    }

    for (const [key, value] of Object.entries(options)) {
      const validatorFn = InitOptions.validators[key as keyof InitOptions];

      if (validatorFn == null && typeof value === 'object' && value !== null) {
        //@ts-ignore
        validateRecursive(value, [...path, key]);
        continue;
      }

      if (typeof validatorFn === 'function') {
        const isValid = validatorFn(value);
        if (!isValid) {
          invalidKeys.push([...path, key].join('.'));
        }
      }
    }
  }

  validateRecursive(initOptions);

  return {
    success: invalidKeys.length === 0,
    invalidKeys,
  };
};
